import React, { useState } from 'react';
import { Document, DocumentFolder, db } from '../../lib/db';
import { FileText, Mail, File, Download, Eye, Trash2, Folder as FolderIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import DocumentPreview from './DocumentPreview';
import { toast } from 'sonner';

interface DocumentListProps {
  documents: Document[];
  folders?: DocumentFolder[];
  dbUserId: string;
  onDocumentDeleted?: () => void;
  showFolders?: boolean;
  compact?: boolean;
}

function DocumentList({ 
  documents, 
  folders = [], 
  dbUserId, 
  onDocumentDeleted,
  showFolders = true,
  compact = false 
}: DocumentListProps) {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <FileText className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-5 h-5 text-blue-600" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText className="w-5 h-5 text-green-600" />;
    if (mimeType.includes('message/rfc822') || mimeType.includes('email')) return <Mail className="w-5 h-5 text-purple-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = (document: Document) => {
    // Use the download API endpoint for better control
    window.open(`/api/download?url=${encodeURIComponent(document.blob_url)}&filename=${encodeURIComponent(document.original_filename)}`, '_blank');
  };

  const handleDelete = async (document: Document) => {
    if (!confirm(`Are you sure you want to delete "${document.original_filename}"?`)) {
      return;
    }

    try {
      // Delete document association first
      await db.documentAssociations.delete(document.id, dbUserId);
      
      // Then delete the document
      await db.documents.delete(document.id, dbUserId);
      
      toast.success('Document deleted successfully');
      onDocumentDeleted?.();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handlePreview = (document: Document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  // Filter documents by folder
  const getFilteredDocuments = async () => {
    if (!selectedFolderId) return documents;
    
    // In a real implementation, you'd filter based on folder associations
    // For now, return all documents
    return documents;
  };

  const filteredDocuments = documents; // For now, show all documents

  if (compact) {
    return (
      <div className="space-y-2">
        {filteredDocuments.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No documents</p>
        ) : (
          filteredDocuments.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                {getFileIcon(doc.mime_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc.original_filename}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(doc.size_bytes)} • {format(new Date(doc.upload_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePreview(doc)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Preview"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
        
        {/* Preview Modal */}
        {showPreview && selectedDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedDocument.original_filename}
                </h3>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setSelectedDocument(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-4">
                <DocumentPreview document={selectedDocument} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {showFolders && folders.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <FolderIcon className="w-5 h-5 text-gray-400" />
            <select
              value={selectedFolderId || ''}
              onChange={(e) => setSelectedFolderId(e.target.value || null)}
              className="flex-1 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Documents</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      <div className="divide-y divide-gray-200">
        {filteredDocuments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p>No documents found</p>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <div key={doc.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  {getFileIcon(doc.mime_type)}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {doc.original_filename}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatFileSize(doc.size_bytes)} • Uploaded {format(new Date(doc.upload_date), 'MMM d, yyyy h:mm a')}
                    </p>
                    {doc.description && (
                      <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePreview(doc)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Preview Modal */}
      {showPreview && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedDocument.original_filename}
              </h3>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setSelectedDocument(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <DocumentPreview document={selectedDocument} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentList;